import * as React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, Popover } from "radix-ui";
import { Button, cn, dialogOverlayClass, useAuth, useIsPhone } from "@ozpanel/ui";
import { StatusPill } from "./PageHeader";
import { useRealtime } from "./useRealtime";
import { NotificationLines } from "./NotificationLines";
import { useNotificationLists } from "./useNotificationLists";
import { useClearNotifications } from "./ClearNotifications";

// The notification bell, for every console.
//
// One component because /me/notifications is one endpoint for three audiences — the scope decides
// what comes back, so the bell does not need to know which console it is in. Three copies would
// be three places to forget the unread count, and the admin console already had a bell fed from
// something else entirely (derived module health), which is why the customer consoles had none.
//
// Two decisions worth stating:
//
//   * THE CONSEQUENCE IS SHOWN, not just the summary. Every warn-or-worse event carries one —
//     the bus refuses an event without it — and it is the half that says why the reader should
//     care. A list of summaries alone is a list of things that sound like someone else's problem.
//   * Reading is EXPLICIT. Opening the panel does not mark anything read: an operator glancing at
//     a bell during an incident has not dealt with anything, and a feed that clears itself on
//     sight cannot be used to hand work between two people.

//   * THE BADGE COUNTS WHAT IS WRONG NOW, not what is unread. The feed keeps one row per fault,
//     opened and ended (internal/episode); until 2026-10-10 nothing recorded a fault ending, so
//     the badge only ever grew — 352 unread after two outages of one node, 16 of the 18 breaches
//     shown already over. Open faults are listed first, folded across the nodes that report them;
//     what ended is history below, saying when and after how long.

export function NotificationBell({
  className,
  audience = "operator",
}: {
  className?: string;
  /** "customer": the account and mailbox consoles. They see the notices, not the machinery — no
   *  "live / reconnecting" stream pill and no paragraph on how faults are folded across servers. */
  audience?: "operator" | "customer";
}) {
  const { client } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const isPhone = useIsPhone();
  const operator = audience === "operator";

  // Polled AND streamed, not one or the other.
  //
  // The poll is the floor: a stream only carries what happens while it is connected, so
  // someone who just opened the page would see nothing until the next event — and "nothing"
  // and "nothing yet" are the same empty panel. The interval is long because the stream is
  // what makes it timely. Open faults are asked for by state (useNotificationLists), so the
  // bell and the Notifications page list the same ones.
  const { recent: q, items, openGroups, earlier: allEarlier, openKnown, unread: unreadN } = useNotificationLists(100, {
    refetchInterval: 60000,
  });

  // The stream makes it immediate. Same endpoint and same scope as the feed: the bus filters by
  // the subscriber's own principal, so a tenant's stream carries a tenant's events and the two
  // cannot disagree about who sees what.
  //
  // Refetching on an event rather than appending it: the event and the stored row are written by
  // the same statement but only the row has an id, and a bell that appended would show an item
  // that cannot be marked read. Asking again costs one small query and keeps one source of truth.
  const { events, connected } = useRealtime();
  const lastSeq = events[0]?.seq ?? 0;
  React.useEffect(() => {
    if (lastSeq > 0) void qc.invalidateQueries({ queryKey: ["me-notifications"] });
  }, [lastSeq, qc]);

  const markRead = useMutation({
    mutationFn: (ids?: number[]) =>
      client.apiFetch("/me/notifications/read", {
        method: "POST",
        body: JSON.stringify(ids && ids.length ? { ids } : { all: true }),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["me-notifications"] }),
  });
  // The confirm lives OUTSIDE the panel, so choosing "Clear all" closes the panel first and the
  // dialog is not torn down with it.
  const clear = useClearNotifications();

  // null: the count could not be read — said in the label, never shown as "none unread".
  const unreadKnown = unreadN != null;
  const unread = unreadN ?? 0;
  const earlier = allEarlier.slice(0, 25);
  // What is wrong now, as the server counts it; null/absent (it could not be counted, or the server
  // predates episodes) falls back to the unread count rather than to "nothing".
  const badge = openKnown ? openGroups.length : unread;
  const hasAny = items.length > 0 || openGroups.length > 0;

  // A failing endpoint renders nothing at all rather than a broken bell. On a node that has not
  // been migrated yet this 404s, and an error badge in the header of every page would be worse
  // than the absence of a feature nobody has been offered yet.
  if (q.isError) return null;

  const label =
    !q.data || unreadKnown || openKnown
      ? badge > 0
        ? openKnown
          ? `${badge} open notifications`
          : `${badge} unread notifications`
        : "Notifications"
      : "Notifications (count unknown)";

  const trigger = (
    <button
      type="button"
      aria-label={label}
      className="relative grid size-10 place-items-center rounded-lg text-muted hover:bg-surface2 hover:text-fg pointer-coarse:size-11"
    >
      <BellIcon />
      {badge > 0 && (
        <span className="absolute right-0.5 top-0.5 grid min-w-[16px] place-items-center rounded-full bg-crit px-1 text-[10px] font-semibold leading-4 text-onaccent">
          {badge > 99 ? "99+" : badge}
        </span>
      )}
    </button>
  );

  const header = (
    <div className="flex items-center justify-between gap-2 border-b border-line px-3 py-2">
      <span className="flex items-center gap-2 text-[13px] font-medium">
        Notifications
        {openKnown ? (
          <span className="text-muted">{openGroups.length} open</span>
        ) : (
          unread > 0 && <span className="text-muted">{unread} unread</span>
        )}
        {/* Whether this feed is LIVE or merely the last poll is load-bearing for an operator: a
            disconnected stream looks identical to a quiet system, and the poll behind it is 60
            seconds wide. A customer is not helped by it, so their consoles leave it out. */}
        {operator && <StatusPill tone={connected ? "ok" : "neutral"}>{connected ? "live" : "reconnecting"}</StatusPill>}
      </span>
      {unread > 0 && (
        <Button size="sm" variant="ghost" onClick={() => markRead.mutate(undefined)}>
          Mark all read
        </Button>
      )}
    </div>
  );

  const body = (
    <>
      <div className={cn("overflow-y-auto overscroll-contain", isPhone ? "min-h-0 flex-1" : "max-h-[60vh]")}>
        {!hasAny ? (
          <div className="px-4 py-8 text-center text-[13px] text-muted">
            Nothing needs your attention.
            <span className="mt-1 block text-[12px]">A failing backup, a certificate that could not be renewed or a mailbox filling up would show here.</span>
          </div>
        ) : (
          <>
            <div className="bg-surface2 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted">
              Open now · {openGroups.length}
            </div>
            <NotificationLines
              audience={audience}
              groups={openGroups}
              onRead={(ids) => markRead.mutate(ids)}
              empty={<div className="px-3 py-4 text-[12.5px] text-muted">Nothing is open. Everything below has ended or simply happened.</div>}
            />
            {earlier.length > 0 && (
              <>
                <div className="bg-surface2 px-3 py-1.5 text-[11px] font-medium uppercase tracking-wide text-muted">
                  Earlier
                </div>
                <NotificationLines audience={audience} groups={earlier} onRead={(ids) => markRead.mutate(ids)} />
              </>
            )}
          </>
        )}
      </div>
      {hasAny && (
        <div className="flex items-center justify-end gap-1 border-t border-line px-2 py-1.5">
          <Button size="sm" variant="ghost" onClick={() => { setOpen(false); clear.request("read"); }}>
            Clear read
          </Button>
          <Button size="sm" variant="danger" onClick={() => { setOpen(false); clear.request("all"); }}>
            Clear all
          </Button>
        </div>
      )}
      {operator && hasAny && (
        <div className="border-t border-line px-3 py-2 text-[11.5px] leading-relaxed text-muted">
          A fault is one line: open until its recovery is announced, then kept below as ended,
          with the recovery itself marked recovered or cleared (never unread). The same fault
          from several nodes is folded into one.
        </div>
      )}
    </>
  );

  // A phone gets a sheet from the bottom, the full width of the screen. A desk gets a popover
  // that Radix keeps inside the viewport. The panel used to hang from the bell's right edge with a
  // fixed width, and the bell sits mid-header — so on a phone it opened 136-167px past the LEFT
  // edge of the screen with its text cut off (measured 2026-11-02).
  return (
    <div className={className}>
      {clear.dialog}
      {isPhone ? (
        <Dialog.Root open={open} onOpenChange={setOpen}>
          <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className={dialogOverlayClass} />
            <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85dvh] flex-col rounded-t-2xl border border-line bg-surface pb-safe outline-none">
              <Dialog.Title className="sr-only">Notifications</Dialog.Title>
              <Dialog.Description className="sr-only">What needs your attention</Dialog.Description>
              {header}
              {body}
              <div className="border-t border-line p-2">
                <Dialog.Close asChild>
                  <Button className="w-full">Close</Button>
                </Dialog.Close>
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      ) : (
        <Popover.Root open={open} onOpenChange={setOpen}>
          <Popover.Trigger asChild>{trigger}</Popover.Trigger>
          <Popover.Portal>
            <Popover.Content
              align="end"
              sideOffset={8}
              collisionPadding={12}
              className="z-50 flex w-[min(400px,calc(100vw-24px))] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-[0_18px_50px_-12px_rgba(0,0,0,.5)]"
            >
              {header}
              {body}
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      )}
    </div>
  );
}

// Inlined rather than pulled from an icon package: the kit is shared by three apps and must not
// force a dependency on one of them for a single glyph.
function BellIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}
